import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { useStrategyMapService } from "../../../../services/strategyMap";
import { refreshStrategymapView } from "../../../../state/actions/strategyMapActions";
import SmapMilestoneCard from "../../../CreateStrategyMap/components/SmapMilestoneMapper/SmapMilestoneCard";
import { SmapMilestoneMapper, StrategyMapMilestone } from "../../../CreateStrategyMap/components/SmapMilestoneMapper/SmapMilestoneMapper";
import { StrategymapSidePanelBodyProps } from "../../StrategymapSidePanel.def";
import StrategyMapSidePanelItem from "../StrategyMapSidePanelItem/StrategyMapSidePanelItem";
import { StrategymapSidePanelSkeleton } from "../StrategymapSidePanelSkeleton/StrategymapSidePanelSkeleton";
import { StrategymapDependencyTab } from "./components/StrategymapDependencyTab/StrategymapDependencyTab";
import { StrategymapSidePanelSearch } from "./StrategymapSidePanelSearch";

export function StrategymapSidePanelBody({
    isSimulation,
    collapsed,
    state,
    filteredProjects,
    projectStatusCounts,
    events,
    strategyMapData
}: StrategymapSidePanelBodyProps) {
    const navigate = useNavigate();
    const { activeTab, search, dependencySearch, isAddingDep, isAddingMilestone, statusDropdownOpen, statusFilters } = state.get;
    const { set: setState } = state;
    // defaultDependency moved to child component
    const [defaultMilestone, setDefaultMilestone] = useState<StrategyMapMilestone | null>(null);
    
    // allDependencies and filteredDependencies moved to child component

    const strategyMapService = useStrategyMapService();

    if (collapsed) return null;

    // onSmapDependencyExpanded moved to child component

    const onMilestoneDelete = async (milestone: StrategyMapMilestone) => {
      if(!strategyMapData) return;
      const updatedMilestones = (strategyMapData.milestones || []).filter(m => 
          !(m._uid === milestone._uid || (m.id && m.id === milestone.id))
      );

      await toast.promise(
          strategyMapService.updateStrategyMap(strategyMapData.id, {
              entities: strategyMapData.entities,
              milestones: updatedMilestones
          }),
          {
              loading: "Deleting milestone...",
              success: "Milestone deleted!",
              error: "Failed to delete milestone"
          }
      );
      refreshStrategymapView();
    };

    // onDeleteDependency moved to child component

    if (activeTab === "dependencies" && strategyMapData) {
        return (
            <StrategymapDependencyTab state={state} strategyMapData={strategyMapData} />
        );
    }

    if (activeTab === "milestones" && strategyMapData) {
        const onMilestoneSave = async (newMilestone: StrategyMapMilestone) => {
            // Validate date range
            if (newMilestone.start_date) {
                const milestoneDate = new Date(newMilestone.start_date).getTime();
                const mapStart = strategyMapData.start_date ? new Date(strategyMapData.start_date).getTime() : null;
                const mapEnd = strategyMapData.end_date ? new Date(strategyMapData.end_date).getTime() : null;

                if ((mapStart && milestoneDate < mapStart) || (mapEnd && milestoneDate > mapEnd)) {
                    toast.error("Milestone date must be within the Strategy Map date range");
                    return;
                }
            }

            setState(prev => ({ ...prev, isAddingMilestone: false }));
            await toast.promise(
                strategyMapService.updateStrategyMap(strategyMapData.id, {
                    entities: strategyMapData.entities,
                    milestones: [...(strategyMapData.milestones || []), newMilestone]
                }),
                {
                    loading: "Saving milestone...",
                    success: "Milestone saved!",
                    error: "Failed to save milestone"
                }
            );
            refreshStrategymapView();
        };

        const onMilestoneUpdate = async (updatedMilestone: StrategyMapMilestone) => {
            if (!strategyMapData) return;
            
            // Validate date range
            if (updatedMilestone.start_date) {
                const milestoneDate = new Date(updatedMilestone.start_date).getTime();
                const mapStart = strategyMapData.start_date ? new Date(strategyMapData.start_date).getTime() : null;
                const mapEnd = strategyMapData.end_date ? new Date(strategyMapData.end_date).getTime() : null;

                if ((mapStart && milestoneDate < mapStart) || (mapEnd && milestoneDate > mapEnd)) {
                    toast.error("Milestone date must be within the Strategy Map date range");
                    return;
                }
            }

            setState(prev => ({ ...prev, isAddingMilestone: false }));
            setDefaultMilestone(null);
            
            const updatedMilestones = (strategyMapData.milestones || []).map(m => 
                (m._uid === updatedMilestone._uid || (m.id && m.id === updatedMilestone.id)) ? updatedMilestone : m
            );

            await toast.promise(
                strategyMapService.updateStrategyMap(strategyMapData.id, {
                    entities: strategyMapData.entities,
                    milestones: updatedMilestones
                }),
                {
                    loading: "Updating milestone...",
                    success: "Milestone updated!",
                    error: "Failed to update milestone"
                }
            );
            refreshStrategymapView();
        };

        return (
            <div className="flex flex-col h-full overflow-hidden">
                <div className="flex items-center sticky top-0 bg-light-surfacePrimary dark:bg-secondary-dark z-10 px-2 pt-2">
                    <h2 className="text-sm font-bold text-light-text-primary dark:text-white flex-1 px-2">
                        Milestones ({strategyMapData.milestones?.length || 0})
                    </h2>
                    <button
                        className="h-[36px] px-3 text-xs font-medium bg-light-surfaceSecondary dark:bg-slate-700 dark:hover:bg-slate-600 text-light-text-primary dark:text-white rounded-xl transition-colors duration-200 whitespace-nowrap hover:bg-light-interactive-hover"
                        onClick={() => {
                            setDefaultMilestone(null);
                            setState(prev => ({ ...prev, isAddingMilestone: !prev.isAddingMilestone }));
                        }}
                    >
                        {isAddingMilestone ? "Cancel" : "+ Add"}
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-2">
                    {isAddingMilestone && (
                        <div className="mb-4">
                            <SmapMilestoneMapper 
                                defaultData={defaultMilestone}
                                onSave={onMilestoneSave}
                                onUpdate={onMilestoneUpdate}
                                minDate={strategyMapData.start_date}
                                maxDate={strategyMapData.end_date}
                                onCancel={() => {
                                    setState(prev => ({ ...prev, isAddingMilestone: false }));
                                    setDefaultMilestone(null);
                                }}
                            />
                        </div>
                    )}

                    <div className="flex flex-col gap-2 pt-2 pb-6">
                        {strategyMapData.milestones && strategyMapData.milestones.length > 0 ? (
                            strategyMapData.milestones.map((m, idx) => (
                                <SmapMilestoneCard 
                                    key={m._uid || idx}
                                    milestone={m}
                                    onEdit={(data) => {
                                        setDefaultMilestone(data);
                                        setState(prev => ({ ...prev, isAddingMilestone: true }));
                                    }}
                                    onRemove={() => onMilestoneDelete(m)}
                                />
                            ))
                        ) : !isAddingMilestone && (
                            <div className="text-center py-10 opacity-50">
                                <p className="text-sm">No milestones found.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    


    // Default: Projects tab
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex items-center sticky top-0 bg-light-surfacePrimary dark:bg-secondary-dark z-10 pr-2">
          <div className="w-full justify-center px-2 py-2">
            <StrategymapSidePanelSearch
              searchValue={search}
              onSearchChange={(val) =>
                setState((prev) => ({ ...prev, search: val }))
              }
              placeholder={`Search for Work Items`}
              statusFilters={statusFilters}
              setStatusFilters={(filters) =>
                setState((prev) => ({ ...prev, statusFilters: filters }))
              }
              statusDropdownOpen={statusDropdownOpen}
              setStatusDropdownOpen={(isOpen) =>
                setState((prev) => ({ ...prev, statusDropdownOpen: isOpen }))
              }
              statusCounts={projectStatusCounts}
              showStatusFilter={true}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto flex flex-col gap-1 p-2 pb-10 relative">
         
         
          {state.get.isLoading || !filteredProjects? <StrategymapSidePanelSkeleton></StrategymapSidePanelSkeleton>: filteredProjects?.map((item, index) => (
            <StrategyMapSidePanelItem
              key={index}
              projectData={item}
              isSimulation={isSimulation}
              onTimelineClick={() => events?.onTimelineViewClick?.(item)}
              onItemClick={() => events?.onEntityClick?.(item)}
            />
          ))}
        </div>
      </div>
    );
}